/**
 * E4 -- an arrow that points the way the reader is going.
 *
 * A hard-coded U+2192 in JSX is a Latin assumption wearing a glyph.
 * On an RTL page "next" is to the LEFT, so a right-pointing arrow beside
 * a link tells an Arabic reader to go back, and the sequence arrows on a
 * drill row ("target -> actual") read as running backwards in time.
 *
 * Mirrored rather than swapped for a reason: the glyph is chosen from
 * the document's direction at render, so a page that flips while open
 * flips its arrows with it, and there is one place to change if a
 * design ever wants a different mark.
 *
 * An arrow inside a CATALOGUE string is a different thing and is
 * deliberately not handled here: that is content, and a translator
 * writing the Arabic writes the arrow that belongs with it.
 */
import { useTranslation } from "react-i18next";

const GLYPH = {
  forward: ["→", "←"],
  back: ["←", "→"],
  up: ["↗", "↖"],
} as const;

/**
 * Round 11 F5 -- the same choice, for the places a component cannot
 * go: a `<select>` option, a title attribute, an aria-label. Exported
 * so those cannot drift from the rendered glyph, which is how the data
 * dictionary and the entity picker one click away came to point
 * different ways.
 */
export function arrowGlyph(kind: keyof typeof GLYPH = "forward", rtl = false): string {
  return GLYPH[kind][rtl ? 1 : 0];
}

export function Arrow({ kind = "forward" }: { kind?: keyof typeof GLYPH }) {
  const { i18n } = useTranslation();
  const rtl = i18n.dir() === "rtl";
  return (
    <span aria-hidden="true" className="marsad-arrow">
      {arrowGlyph(kind, rtl)}
    </span>
  );
}
